import type { ColumnDef } from "@tanstack/react-table";
import type { AgendaItem, AgendaModule } from "@repo/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/Base/DropDown";
import { Button } from "@repo/ui/components/Base/Button";
import { MoreHorizontal, SquareArrowOutUpRight } from "lucide-react";
import { DataTableColumnHeader } from "@repo/ui/components/Base/DataTableColumnHeader";
import { Chip } from "@repo/ui/components/Base/Chip";
import { AgendaItemSummaryItem } from "../agendaItems/AgendaItemSummaryItem";
import { RoleProtected } from "../common/RoleProtected";
import { AgendaSummaryItem } from "../agendas/AgendaSummaryItem";
import { CappedLinkList } from "../common/CappedLinkList";
import { AppUrlPath } from "@/lib/AppUrlPath";
import {
  createCreatedAtColumn,
  createCreatedByColumn,
  createDescriptionColumn,
  createIdColumn,
  createNameColumn,
  createStatusColumn,
  createTagColumn,
  createTeamColumn,
  createUpdatedAtColumn,
  createUpdatedByColumn,
  getUsedByFilterFn,
} from "../dataTable/columns";
import type { TableLayoutState } from "@repo/ui/components/Base/DataTable";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useMemo } from "react";
import { useUserStore } from "@/stores/userStore";
import { cn } from "@repo/shared/utils";
import type { AgendaModuleDto } from "@/repositories/agendaModuleRepo";
import { agendaRepo, type AgendaDto } from "@/repositories/agendaRepo";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";

export const AgendaModuleTemplateColumn: ColumnDef<AgendaModule> = {
  accessorKey: "template",
  meta: {
    label: "Is Template",
    filterVariant: "customSelect",
    customSelectOptions: [
      { label: "Is Template", value: "template" },
      { label: "Not a Template", value: "notTemplate" },
    ],
  },
  filterFn: (row, _, value) => {
    if (value === "template") {
      return row.original.template ?? false;
    } else if (value === "notTemplate") {
      return !row.original.template;
    }
    return true;
  },
  sortingFn: (a, b) => {
    const isTemplateA = a.original.template;
    const isTemplateB = b.original.template;
    return isTemplateA && !isTemplateB
      ? -1
      : !isTemplateA && isTemplateB
        ? 1
        : 0;
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Is Template" />
  ),
  cell: ({ row }) => {
    const doc = row.original;
    return (
      <div className="flex flex-row flex-wrap">
        {doc.template && <Chip>Template</Chip>}
      </div>
    );
  },
};

export const createAgendaModuleUsedByColumn = (
  tenantId: string
): ColumnDef<AgendaModule> => ({
  id: "usedBy",
  accessorKey: "parentIds",
  filterFn: "async",
  meta: {
    label: "Agendas Used In",
    filterVariant: "usedBy",
    asyncFilterFn: getUsedByFilterFn<AgendaModuleDto, AgendaDto>(
      "parentIds",
      agendaRepo,
      tenantId,
      "name"
    ),
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Agendas Used In" noSort />
  ),
  cell: ({ row }) => {
    const agendaModule = row.original;

    return (
      <CappedLinkList
        ids={agendaModule.parentIds}
        morePath={new AppUrlPath(tenantId).getAgendaModulePath(agendaModule.id)}
      >
        {(agendaId) => (
          <AgendaSummaryItem
            key={agendaId}
            tenantId={tenantId}
            agendaId={agendaId}
          />
        )}
      </CappedLinkList>
    );
  },
});

export const createAgendaModuleAgendaItemsColumn = (
  tenantId: string
): ColumnDef<AgendaModule> => ({
  id: "agendaItems",
  accessorKey: "agendaItemIds",
  filterFn: "async",
  meta: {
    label: "Agenda Items",
    filterVariant: "usedBy",
    asyncFilterFn: getUsedByFilterFn<AgendaModule, AgendaItem>(
      "agendaItemIds",
      agendaItemRepo,
      tenantId,
      "name"
    ),
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Agendas Items" noSort />
  ),
  cell: ({ row }) => {
    const agendaModule = row.original;

    return (
      <CappedLinkList
        ids={agendaModule.agendaItemIds}
        morePath={new AppUrlPath(tenantId).getAgendaModulePath(agendaModule.id)}
      >
        {(agendaModuleId) => (
          <AgendaItemSummaryItem
            key={agendaModuleId}
            tenantId={tenantId}
            agendaItemId={agendaModuleId}
          />
        )}
      </CappedLinkList>
    );
  },
});

export const useColumns = ({
  tenantId,
  handleQuickEdit,
  handleEdit,
  handleArchive,
  handleView,
  handleCopy: _handleCopy,
}: {
  tenantId: string;
  handleQuickEdit: (id: string) => void;
  handleEdit: (id: string) => void;
  handleArchive: (id: string) => void;
  handleView: (id: string) => void;
  handleCopy?: (id: string) => void;
}) => {
  const hadHandleCopy = !!_handleCopy;

  handleQuickEdit = useFutureCallback(handleQuickEdit);
  handleEdit = useFutureCallback(handleEdit);
  handleArchive = useFutureCallback(handleArchive);
  handleView = useFutureCallback(handleView);
  const handleCopy = useFutureCallback(_handleCopy ?? (() => {}));

  const col: ColumnDef<AgendaModule>[] = useMemo(
    () => [
      {
        id: "actions",
        header: "",
        cell: function Cell({ row }) {
          const team = useUserStore((s) => s.activeTeam);
          const isFullOrg = !team || team === "org";

          const agendaModule = row.original;
          return (
            <div className="flex flex-row align-middle gap-1 justify-start">
              <Button
                variant="ghost"
                tooltip={
                  isFullOrg ? "Pick a team first" : "Open this agenda section"
                }
                className={cn(
                  "h-8 w-8 p-0",
                  isFullOrg && "opacity-50 cursor-not-allowed"
                )}
                disabled={isFullOrg}
                onClick={() => handleView(agendaModule.id)}
              >
                <SquareArrowOutUpRight />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <RoleProtected minRole="edit">
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      disabled={isFullOrg}
                      tooltip={isFullOrg ? "Pick a team first" : ""}
                      onClick={() => handleQuickEdit(agendaModule.id)}
                    >
                      Quick Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={isFullOrg}
                      tooltip={isFullOrg ? "Pick a team first" : ""}
                      onClick={() => handleEdit(agendaModule.id)}
                    >
                      Edit
                    </DropdownMenuItem>
                    {hadHandleCopy && (
                      <DropdownMenuItem
                        disabled={isFullOrg}
                        tooltip={
                          isFullOrg
                            ? "Pick a team first"
                            : "Copy this template to other team"
                        }
                        onClick={() => handleCopy(agendaModule.id)}
                      >
                        Copy to
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => handleArchive(agendaModule.id)}
                    >
                      Archive
                    </DropdownMenuItem>
                  </RoleProtected>
                  <DropdownMenuItem
                    onClick={() =>
                      navigator.clipboard.writeText(agendaModule.id)
                    }
                  >
                    Copy agenda item ID
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableHiding: false,
      },
      createIdColumn(),
      createNameColumn(),
      createTagColumn(),
      createDescriptionColumn(),
      createStatusColumn(),
      createCreatedAtColumn(),
      createCreatedByColumn(tenantId),
      createUpdatedAtColumn(),
      createUpdatedByColumn(tenantId),
      createTeamColumn(tenantId),
      createAgendaModuleUsedByColumn(tenantId),
      createAgendaModuleAgendaItemsColumn(tenantId),
    ],
    [
      tenantId,
      hadHandleCopy,
      handleView,
      handleQuickEdit,
      handleEdit,
      handleCopy,
      handleArchive,
    ]
  );
  return col;
};

export const layoutDefaults: TableLayoutState = {
  columnVisibility: {
    id: false,
    updatedAt: false,
    usedBy: false,
  },
};
